/* ==========================================================================
   carousel.js — DESIGN-SPEC-v2 §5. Generic [data-carousel] behaviour:
   - autoplay from data-interval (ms, default 6000); data-autoplay="false",
     a single slide, or prefers-reduced-motion disables it
   - pause on hover and on focus within; resumes on leave/blur
   - prev/next arrows ([data-carousel-prev] / [data-carousel-next])
   - dots ([data-carousel-dot], document order = slide order)
   - pointer-event swipe, 40px horizontal threshold (vertical pans ignored)
   - keyboard: ArrowLeft/ArrowRight anywhere inside the carousel
   - m2Fade replays via the .is-active class toggle (CSS-driven)
   - [data-carousel-track] (or the root) is aria-live: polite while paused,
     off while auto-rotating
   Vanilla, no libraries. Safe to include once per page.
   ========================================================================== */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 40;
  var reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  /* Module-level registry + ONE document/matchMedia listener pair for every
     carousel on the page. Per-instance listeners on document would stack on
     each shopify:section:load re-render and pin detached roots in memory;
     instead the shared handlers walk the registry and prune disconnected
     roots as they go. */
  var registry = [];

  function refreshAll() {
    for (var i = registry.length - 1; i >= 0; i--) {
      if (!registry[i].root.isConnected) {
        registry[i].stop();
        registry.splice(i, 1);
        continue;
      }
      registry[i].play();
    }
  }

  document.addEventListener('visibilitychange', refreshAll);
  if (reducedMotion) {
    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', refreshAll);
    } else if (reducedMotion.addListener) {
      reducedMotion.addListener(refreshAll);
    }
  }

  function initCarousel(root) {
    if (root.__ecolineCarousel) return;

    var slides = Array.prototype.slice.call(
      root.querySelectorAll('[data-carousel-slide]')
    );
    if (!slides.length) return;

    var dots = Array.prototype.slice.call(
      root.querySelectorAll('[data-carousel-dot]')
    );
    var prevBtn = root.querySelector('[data-carousel-prev]');
    var nextBtn = root.querySelector('[data-carousel-next]');
    var liveRegion = root.querySelector('[data-carousel-track]') || root;

    var interval = parseInt(root.getAttribute('data-interval'), 10);
    if (isNaN(interval)) interval = 6000;
    var autoplayAllowed =
      slides.length > 1 &&
      interval > 0 &&
      root.getAttribute('data-autoplay') !== 'false';

    var index = 0;
    slides.forEach(function (slide, i) {
      if (slide.classList.contains('is-active')) index = i;
    });

    var timer = null;
    var hovered = false;
    var focused = false;

    function render() {
      slides.forEach(function (slide, i) {
        var active = i === index;
        slide.classList.toggle('is-active', active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function goTo(i) {
      index = ((i % slides.length) + slides.length) % slides.length;
      render();
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      liveRegion.setAttribute('aria-live', 'polite');
    }

    // Single autoplay gatekeeper: (re)starts the timer only when every
    // condition allows it, otherwise leaves the carousel paused.
    function play() {
      stop();
      if (!root.isConnected) return;
      if (!autoplayAllowed) return;
      if (reducedMotion && reducedMotion.matches) return;
      if (hovered || focused || document.hidden) return;
      timer = setInterval(function () {
        if (!root.isConnected) {
          stop();
          return;
        }
        goTo(index + 1);
      }, interval);
      liveRegion.setAttribute('aria-live', 'off');
    }

    // Manual navigation also resets the autoplay clock.
    function interact(fn) {
      fn();
      play();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        interact(function () { goTo(index - 1); });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        interact(function () { goTo(index + 1); });
      });
    }
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        interact(function () { goTo(i); });
      });
    });

    root.addEventListener('keydown', function (e) {
      if (e.defaultPrevented) return;
      var tag = e.target && e.target.tagName;
      if (tag && /^(input|textarea|select)$/i.test(tag)) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        interact(function () { goTo(index - 1); });
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        interact(function () { goTo(index + 1); });
      }
    });

    root.addEventListener('mouseenter', function () { hovered = true; play(); });
    root.addEventListener('mouseleave', function () { hovered = false; play(); });
    root.addEventListener('focusin', function () { focused = true; play(); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) {
        focused = false;
        play();
      }
    });

    /* visibilitychange + reduced-motion re-checks come from the shared
       module-scope listeners via this registry entry (see refreshAll). */
    registry.push({ root: root, play: play, stop: stop });

    // Swipe — pointer events, 40px horizontal threshold. touch-action: pan-y
    // (section CSS) keeps vertical scrolling native; a horizontal swipe that
    // crossed the threshold must not also follow the link under the finger.
    var startX = 0;
    var startY = 0;
    var tracking = false;
    var swiped = false;

    root.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      startX = e.clientX;
      startY = e.clientY;
      tracking = true;
      swiped = false;
    });
    root.addEventListener('pointermove', function (e) {
      if (!tracking) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (Math.abs(dx) >= SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        tracking = false;
        swiped = true;
        interact(function () { goTo(dx < 0 ? index + 1 : index - 1); });
      }
    });
    root.addEventListener('pointerup', function () { tracking = false; });
    root.addEventListener('pointercancel', function () { tracking = false; });
    root.addEventListener(
      'click',
      function (e) {
        if (swiped) {
          e.preventDefault();
          e.stopPropagation();
          swiped = false;
        }
      },
      true
    );

    render();
    play();

    root.__ecolineCarousel = { goTo: goTo, play: play, stop: stop, slides: slides };
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-carousel]');
    Array.prototype.forEach.call(nodes, initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: initialise re-rendered sections, and show the slide whose
  // block the merchant selected (autoplay stays off while they work on it).
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var slide =
      e.target && e.target.closest ? e.target.closest('[data-carousel-slide]') : null;
    if (!slide) return;
    var root = slide.closest('[data-carousel]');
    if (!root || !root.__ecolineCarousel) return;
    var api = root.__ecolineCarousel;
    var i = api.slides.indexOf(slide);
    if (i > -1) {
      api.stop();
      api.goTo(i);
    }
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var slide =
      e.target && e.target.closest ? e.target.closest('[data-carousel-slide]') : null;
    if (!slide) return;
    var root = slide.closest('[data-carousel]');
    if (root && root.__ecolineCarousel) root.__ecolineCarousel.play();
  });
})();
